import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ContractShell, GREEN, SignaturePad } from '../components/ContractKit';
import ContractFields from '../components/ContractFields';

const TITLES = {
  windows: 'WINDOWS ADDENDUM',
  colors: 'NOTICE OF CHOSEN COLORS',
  roofing: 'ROOFING ADDENDUM',
  coating: 'EXTERIOR COATING ADDENDUM',
  lien: 'MECHANICS LIEN',
  'change-order': 'CHANGE ORDER ADDENDUM',
  hia: 'CALIFORNIA HOME IMPROVEMENT AGREEMENT',
  cancel: 'THREE-DAY RIGHT TO CANCEL',
};

export default function PublicSign() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, type: null });
  const [f, setF] = useState({ ack: {}, sigs: {} });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [signMode, setSignMode] = useState('all'); // 'all' | 'each'
  const [consented, setConsented] = useState(false);   // ESIGN consent gate
  const [consentAt, setConsentAt] = useState(null);
  const [agree, setAgree] = useState({ esign: false, legal: false, identity: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setSig = (type, v) => setF((s) => ({ ...s, sigs: { ...(s.sigs || {}), [type]: v } }));

  useEffect(() => {
    api.get(`/contracts/public/${token}`)
      .then((res) => {
        setState({ loading: false, error: null, type: res.data.contract_type, docs: res.data.docs || [{ type: res.data.contract_type, name: res.data.doc_name }], read_only: res.data.read_only, file_no: res.data.file_no });
        if (res.data.prefill && Object.keys(res.data.prefill).length) {
          setF((s) => ({ ...s, ...res.data.prefill }));
        }
      })
      .catch((e) => setState({ loading: false, error: e?.response?.data?.error || 'This link could not be opened.', type: null }));
  }, [token]);

  async function submit() {
    const docs = state.docs || [{ type: state.type }];
    if (signMode === 'all') {
      if (!f.buyerSig) { toast.error('Please add your signature to continue.'); return; }
    } else {
      const missing = docs.find(d => !(f.sigs || {})[d.type]);
      if (missing) { toast.error(`Please sign “${missing.name}” to continue.`); return; }
    }
    setSaving(true);
    try {
      await api.post(`/contracts/public/${token}`, {
        ...f, sign_mode: signMode,
        consent: { accepted: true, at: consentAt, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not submit. The link may have expired.');
    } finally { setSaving(false); }
  }

  const frame = (children) => (
    <div style={{ minHeight: '100vh', background: '#f4f6f5', padding: '24px 12px' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 22, color: GREEN, letterSpacing: 1 }}>CPR HOME SOLUTIONS</div>
          <div style={{ fontSize: 12, color: '#888' }}>Secure document signing</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 22px', boxShadow: '0 2px 14px rgba(0,0,0,0.06)' }}>
          {children}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#aaa', marginTop: 14 }}>
          This is a secure one-time signing link. Do not share it.
        </div>
      </div>
    </div>
  );

  if (state.loading) return frame(<div style={{ textAlign: 'center', color: '#888', padding: 30 }}>Loading your document…</div>);

  if (state.error) return frame(
    <div style={{ textAlign: 'center', padding: 30 }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
      <div style={{ fontWeight: 700, fontSize: 16, color: '#c62828', marginBottom: 6 }}>{state.error}</div>
      <div style={{ fontSize: 13, color: '#888' }}>Please contact your CPR Home Solutions representative for a new link.</div>
    </div>
  );

  if (done) return frame(
    <div style={{ textAlign: 'center', padding: 30 }}>
      <div style={{ fontSize: 46, marginBottom: 10 }}>✅</div>
      <div style={{ fontWeight: 700, fontSize: 18, color: GREEN, marginBottom: 6 }}>Thank you — your document is signed.</div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 18 }}>A copy has been securely sent to CPR Home Solutions.</div>
      <a href={`/api/contracts/public/${token}/pdf`} target="_blank" rel="noopener noreferrer"
        style={{ display: 'inline-block', background: GREEN, color: '#fff', fontWeight: 700, fontSize: 14, padding: '11px 22px', borderRadius: 9, textDecoration: 'none' }}>
        ⬇ Download your signed copy
      </a>
      <div style={{ fontSize: 12, color: '#aaa', marginTop: 14 }}>You may close this page once you have your copy.</div>
    </div>
  );

  const docsForGate = state.docs || [{ type: state.type, name: 'Document' }];
  if (!consented) return frame(
    <div>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 19, color: GREEN, letterSpacing: 0.5 }}>BEFORE YOU SIGN</div>
        <div style={{ fontSize: 13, color: '#777', marginTop: 4 }}>Please read and accept the following</div>
      </div>

      <div style={{ background: '#fff8e6', border: '1px solid #f0d99a', borderRadius: 10, padding: '12px 15px', marginBottom: 16, fontSize: 13.5, color: '#6b4e10', lineHeight: 1.55 }}>
        You are about to sign a <b>legally binding contract</b>. Once signed, {docsForGate.length === 1 ? 'this document creates' : 'these documents create'} real
        obligations for you and for CPR Home Solutions, Inc., including payment obligations.
        Please read {docsForGate.length === 1 ? 'it' : 'each document'} carefully before signing.
      </div>

      <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6, marginBottom: 6, fontWeight: 700 }}>
        You are being asked to sign {docsForGate.length === 1 ? 'this document' : `these ${docsForGate.length} documents`}:
      </div>
      <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: 13, color: '#444', lineHeight: 1.8 }}>
        {docsForGate.map(d => <li key={d.type}>{d.name || TITLES[d.type] || d.type}</li>)}
      </ul>

      <div style={{ border: '1px solid #e3e7e5', borderRadius: 10, padding: '13px 15px', marginBottom: 16, fontSize: 12.5, color: '#555', lineHeight: 1.6, background: '#fafbfa' }}>
        <div style={{ fontWeight: 700, color: '#333', marginBottom: 6, fontSize: 13 }}>Agreement to sign electronically</div>
        By continuing you agree that your electronic signature is the legal equivalent of your handwritten
        signature, and that CPR Home Solutions, Inc. may provide these documents to you electronically.
        <div style={{ marginTop: 8 }}>
          You have the right to receive <b>paper copies</b> of these documents instead, and the right to
          <b> withdraw your consent</b> to sign electronically, at no cost, by contacting CPR Home Solutions at
          (626) 522-9099 before you sign. To sign electronically you need a device with a web browser and
          internet access, and a way to view and save PDF files. You will be able to download a signed copy
          of {docsForGate.length === 1 ? 'the document' : 'every document'} immediately after signing, and we recommend saving it for your records.
        </div>
        <div style={{ marginTop: 8 }}>
          You also keep your <b>three-day right to cancel</b> as described in the documents. Signing here does
          not waive that right.
        </div>
      </div>

      {[
        ['legal', <>I understand {docsForGate.length === 1 ? 'this is a legally binding document' : 'these are legally binding documents'}, and I have read {docsForGate.length === 1 ? 'it' : 'them'} or will read {docsForGate.length === 1 ? 'it' : 'them'} before signing.</>],
        ['esign', <>I agree to sign electronically, and I understand I may request paper copies or withdraw this consent instead.</>],
        ['identity', <>I am the person named in {docsForGate.length === 1 ? 'the document' : 'the documents'}, or I am authorized to sign on their behalf.</>],
      ].map(([k, label]) => (
        <label key={k} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '9px 11px', border: '1px solid ' + (agree[k] ? '#bcd9c9' : '#e3e7e5'), background: agree[k] ? '#f2f9f5' : '#fff', borderRadius: 9, marginBottom: 8 }}>
          <input type="checkbox" checked={agree[k]} onChange={() => setAgree(a => ({ ...a, [k]: !a[k] }))}
            style={{ marginTop: 2, width: 17, height: 17, accentColor: GREEN, flexShrink: 0, cursor: 'pointer' }} />
          <span style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{label}</span>
        </label>
      ))}

      <button type="button"
        disabled={!(agree.legal && agree.esign && agree.identity)}
        onClick={() => { setConsentAt(new Date().toISOString()); setConsented(true); }}
        style={{ width: '100%', marginTop: 10, padding: '13px', borderRadius: 10, border: 'none',
          background: (agree.legal && agree.esign && agree.identity) ? GREEN : '#c9cfcc',
          color: '#fff', fontSize: 15, fontWeight: 700,
          cursor: (agree.legal && agree.esign && agree.identity) ? 'pointer' : 'not-allowed' }}>
        I agree — continue to {docsForGate.length === 1 ? 'the document' : 'the documents'} →
      </button>
      <div style={{ textAlign: 'center', fontSize: 11.5, color: '#999', marginTop: 10 }}>
        Questions? Call CPR Home Solutions at (626) 522-9099 before signing.
      </div>
    </div>
  );

  const docs = state.docs || [{ type: state.type, name: 'Document' }];
  const multi = docs.length > 1;
  return frame(
    <ContractShell title={multi ? 'DOCUMENT PACKET' : (TITLES[state.type] || 'CONTRACT')} onGenerate={submit} saving={saving} savedDoc={null}
      buttonLabel={signMode === 'all' ? `Sign all ${docs.length} & Submit →` : 'Submit signed documents →'}>
      <div style={{ background: '#eef6f1', border: '1px solid #cfe6da', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#15543b' }}>
        🔒 The following {docs.length === 1 ? 'document is' : docs.length + ' documents are'} pre-filled and locked. Please review {docs.length === 1 ? 'it' : 'each one'} and sign.
        {state.file_no ? <span style={{ float: 'right', fontWeight: 700 }}>File #{state.file_no}</span> : null}
      </div>

      {/* signing mode toggle (only matters for packets of 2+ docs) */}
      {multi && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <button type="button" onClick={() => setSignMode('all')}
            style={{ flex: 1, border: '1px solid ' + (signMode === 'all' ? '#0D3B2E' : '#ddd'), background: signMode === 'all' ? '#0D3B2E' : '#fff', color: signMode === 'all' ? '#fff' : '#333', borderRadius: 9, padding: '9px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            ✍️ Sign all at once
            <span style={{ display: 'block', fontWeight: 400, fontSize: 11, opacity: 0.85 }}>One signature applies to every document</span>
          </button>
          <button type="button" onClick={() => setSignMode('each')}
            style={{ flex: 1, border: '1px solid ' + (signMode === 'each' ? '#0D3B2E' : '#ddd'), background: signMode === 'each' ? '#0D3B2E' : '#fff', color: signMode === 'each' ? '#fff' : '#333', borderRadius: 9, padding: '9px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            🖊️ Sign each document
            <span style={{ display: 'block', fontWeight: 400, fontSize: 11, opacity: 0.85 }}>Add a signature to each one separately</span>
          </button>
        </div>
      )}

      {docs.map((d, i) => (
        <div key={d.type} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 14, color: '#0D3B2E', background: '#f3f5f4', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>
            <span style={{ background: '#0D3B2E', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>{i + 1}</span>
            {d.name}
          </div>
          <ContractFields type={d.type} f={f} set={set} readOnly={true} hideSignature={true} />
          {signMode === 'each' && (
            <div style={{ marginTop: 10, background: '#f9fbfa', border: '1px solid #e4e9e7', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12.5, color: '#0D3B2E', marginBottom: 6 }}>Sign {d.name}</div>
              <SignaturePad label="Signature" onChange={(v) => setSig(d.type, v)} />
            </div>
          )}
        </div>
      ))}

      {signMode === 'all' && (
        <div style={{ borderTop: '2px solid #0D3B2E', paddingTop: 16, marginTop: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#0D3B2E', marginBottom: 4 }}>✍️ Your signature</div>
          <div style={{ fontSize: 12.5, color: '#777', marginBottom: 10 }}>By signing below, you agree to and sign {multi ? `all ${docs.length} documents` : 'the document'} above.</div>
          <SignaturePad label="Signature" onChange={(v) => set('buyerSig', v)} />
          <div style={{ marginTop: 10 }}>
            <SignaturePad label="Initials (where required)" onChange={(v) => set('initials', v)} />
          </div>
        </div>
      )}
    </ContractShell>
  );
}
